import { canModerateFeed } from '../../../../helpers/feed-moderation';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { staffBeta } from '../../../../api/feed-beta.api';
import { useAppSelector } from '../../../../hooks';
import { authSelector } from '../../../../store/slices';

export default function FeedBetaProgram() {
  const { user } = useAppSelector(authSelector);
  const allowed = canModerateFeed(user?.admin);
  const client = useQueryClient();
  const dashboard = useQuery({
    queryKey: ['staff-beta', user?.id],
    queryFn: staffBeta.dashboard,
    enabled: allowed,
    refetchInterval: 30000,
  });
  const [minReviews, setMinReviews] = useState('1');
  const [invitationLimit, setInvitationLimit] = useState('100');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirm, setConfirm] = useState<'rewards' | 'end' | 'launch' | null>(
    null
  );
  const program = dashboard.data?.program;
  const configuredMinReviews = program?.minReviews;
  const configuredInvitationLimit = program?.invitationLimit;
  useEffect(() => {
    if (
      configuredMinReviews !== undefined &&
      configuredInvitationLimit !== undefined
    ) {
      setMinReviews(String(configuredMinReviews));
      setInvitationLimit(String(configuredInvitationLimit));
    }
  }, [configuredMinReviews, configuredInvitationLimit]);
  const run = async (action: () => Promise<unknown>, success: string) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      setConfirm(null);
      if (success) setNotice(success);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['staff-beta'] }),
        client.invalidateQueries({ queryKey: ['staff-feed'] }),
      ]);
    } catch {
      setError('The action could not be saved. Refresh and retry.');
    } finally {
      setBusy(false);
    }
  };
  if (!allowed)
    return (
      <p className="p-8">
        You do not have permission to manage the review program.
      </p>
    );
  if (dashboard.isLoading) return <p className="p-8">Loading beta program…</p>;
  if (dashboard.isError || !dashboard.data)
    return (
      <div className="p-8">
        <p>
          Couldn’t load the program. Check that the feed is configured on the
          server.
        </p>
        <button onClick={() => dashboard.refetch()}>Retry</button>
      </div>
    );
  const data = dashboard.data;
  const updateInvites = () => {
    const minimum = Number(minReviews);
    const limit = Number(invitationLimit);
    if (
      !Number.isInteger(minimum) ||
      minimum < 1 ||
      minimum > 100000 ||
      !Number.isInteger(limit) ||
      limit < 1 ||
      limit > 1000000
    ) {
      setError(
        'Enter a minimum of 1–100,000 reviews and an invitation limit of 1–1,000,000 users.'
      );
      return;
    }
    run(
      () => staffBeta.update({ minReviews: minimum, invitationLimit: limit }),
      'Invitation criteria saved.'
    );
  };
  return (
    <section className="p-6 lg:p-10 max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold">500chow Public Review Program</h1>
      <p className="text-neutral-500 mt-2">
        Recruit early reviewers, fill the feed, and release ₦500 coupons when
        you’re ready.
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 my-8">
        {[
          ['Beta members', data.members],
          ['Shared reviews', data.sharedReviews],
          ['Approved reviews', data.approvedReviews],
          ['Awaiting approval', data.pendingReviews],
          ['Coupons pending', data.rewardsPending],
        ].map(([label, value]) => (
          <div key={label} className="border rounded-lg p-4">
            <p className="text-sm text-neutral-500">{label}</p>
            <p className="text-2xl font-semibold mt-2">{value}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <article className="border rounded-lg p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold">Import existing reviews</h2>
          <p className="my-3 text-sm text-neutral-500">
            Import up to 25 existing reviews rated 3–5 stars, including their
            written food comments and selected tags. Jev checks each post before
            publication. Privacy choices are respected. New reviews are never
            imported automatically.
          </p>
          <button
            disabled={busy}
            onClick={() =>
              run(async () => {
                const result = await staffBeta.importReviews();
                setNotice(
                  `${result.published} published; ${result.pending} awaiting approval; ${result.skipped} skipped; ${result.failed} failed.` +
                    (result.hasMore
                      ? ' More reviews remain. Run another import batch when ready.'
                      : ' No eligible reviews remain.')
                );
              }, '')
            }
            className="border rounded p-3"
          >
            Import existing reviews
          </button>
        </article>
        <article className="border rounded-lg p-6">
          <h2 className="text-lg font-semibold">Recruitment</h2>
          <p className="my-3">
            {program?.endedAt
              ? `Program ended ${new Date(program.endedAt).toLocaleString()}`
              : program?.recruitmentActive
              ? 'Invitations are active.'
              : 'Invitations are paused.'}
          </p>
          <p className="text-sm text-neutral-500 mb-5">
            Users who choose “Maybe later” see the invitation again after seven
            days.
          </p>
          <div className="mb-5">
            <label htmlFor="beta-min-reviews" className="block">
              Reviewed orders needed to qualify
            </label>
            <input
              id="beta-min-reviews"
              aria-describedby="beta-min-reviews-help"
              type="number"
              min="1"
              max="100000"
              value={minReviews}
              onChange={(event) => setMinReviews(event.target.value)}
              className="block border rounded p-3 mt-2 w-full"
            />
            <p
              id="beta-min-reviews-help"
              className="text-sm text-neutral-500 mt-2"
            >
              A user must have reviewed at least this many orders to qualify.
              Set 1 to include anyone with a reviewed order.
            </p>
          </div>
          <div className="mb-5">
            <label htmlFor="beta-invitation-limit" className="block">
              Number of top reviewers to invite
            </label>
            <input
              id="beta-invitation-limit"
              aria-describedby="beta-invitation-limit-help"
              type="number"
              min="1"
              max="1000000"
              value={invitationLimit}
              onChange={(event) => setInvitationLimit(event.target.value)}
              className="block border rounded p-3 mt-2 w-full"
            />
            <p
              id="beta-invitation-limit-help"
              className="text-sm text-neutral-500 mt-2"
            >
              Invite this many eligible users, starting with those who have
              reviewed the most orders. Already joined users count toward this
              total; increase it to invite more users.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              disabled={busy || !!program?.endedAt}
              onClick={updateInvites}
              className="border rounded p-3"
            >
              Save invitation criteria
            </button>
            <button
              disabled={busy || !!program?.endedAt}
              onClick={() =>
                run(
                  () =>
                    staffBeta.update({
                      recruitmentActive: !program?.recruitmentActive,
                    }),
                  'Recruitment updated.'
                )
              }
              className="border rounded p-3"
            >
              {program?.recruitmentActive
                ? 'Pause invitations'
                : 'Start invitations'}
            </button>
          </div>
        </article>
        <article className="border rounded-lg p-6">
          <h2 className="text-lg font-semibold">Public launch and rewards</h2>
          <p className="my-3">
            The public feed is {program?.feedEnabled ? 'live' : 'closed'}.
          </p>
          <p className="text-sm text-neutral-500 mb-5">
            Enabling the feed makes approved public posts available to users.
            Joining alone never issues a coupon.
          </p>
          <div className="flex flex-col items-start gap-4">
            <button
              disabled={busy}
              onClick={() =>
                program?.feedEnabled
                  ? run(
                      () => staffBeta.update({ feedEnabled: false }),
                      'Public feed closed.'
                    )
                  : setConfirm('launch')
              }
              className="border rounded p-3"
            >
              {program?.feedEnabled
                ? 'Close public feed'
                : 'Enable public feed'}
            </button>
            <button
              disabled={busy || !data.rewardsPending}
              onClick={() => setConfirm('rewards')}
              className="border rounded p-3"
            >
              Issue ₦500 coupons ({data.rewardsPending} pending)
            </button>
            <p className="text-sm text-neutral-500">
              {data.rewarded} members rewarded. Coupons are personal, worth
              ₦500, and valid for one use. Each action issues up to 100; repeat
              for remaining members.
            </p>
            <button
              disabled={busy || !!program?.endedAt}
              onClick={() => setConfirm('end')}
              className="border rounded p-3 text-red-700"
            >
              End beta program
            </button>
          </div>
        </article>
      </div>
      {confirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirm program action"
          className="border border-orange-300 bg-orange-50 rounded-lg p-6 mt-6"
        >
          <p>
            {confirm === 'launch'
              ? `Launch the public feed with ${data.approvedReviews} approved beta reviews?`
              : confirm === 'rewards'
              ? `Issue a personal ₦500 coupon now to the next ${Math.min(
                  100,
                  data.rewardsPending
                )} unrewarded members? Previously rewarded members will be skipped.`
              : 'End recruitment permanently? Existing sharing choices, feed visibility, and outstanding rewards remain available.'}
          </p>
          <div className="flex gap-4 mt-4">
            <button
              disabled={busy}
              onClick={() =>
                confirm === 'launch'
                  ? run(
                      () => staffBeta.update({ feedEnabled: true }),
                      'Public feed enabled.'
                    )
                  : confirm === 'end'
                  ? run(staffBeta.end, 'Beta program ended.')
                  : run(async () => {
                      const result = await staffBeta.rewards();
                      setNotice(
                        `${result.issued} coupons issued; ${result.remaining} still pending.`
                      );
                    }, '')
              }
              className="border bg-white rounded p-3"
            >
              {busy ? 'Saving…' : 'Confirm'}
            </button>
            <button
              disabled={busy}
              onClick={() => setConfirm(null)}
              className="p-3"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {!!error && (
        <p role="alert" className="text-red-700 mt-5">
          {error}
        </p>
      )}
      {!!notice && (
        <p role="status" className="text-green-700 mt-5">
          {notice}
        </p>
      )}
      <h2 className="text-lg font-semibold mt-10 mb-4">
        Most active reviewers
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr>
              <th className="p-3">Username</th>
              <th className="p-3">Reviewed orders</th>
              <th className="p-3">Beta status</th>
            </tr>
          </thead>
          <tbody>
            {data.topReviewers.map((user) => (
              <tr key={user.userId} className="border-t">
                <td className="p-3">@{user.username}</td>
                <td className="p-3">{user.reviewCount}</td>
                <td className="p-3">{user.joined ? 'Joined' : 'Not joined'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
